import type { FilterValue } from '../components/schedule/ScheduleFilters';

/** No filters at all — what "Reset" and "Clear filters" stage. */
export const EMPTY_FILTERS: FilterValue = {
  region: '',
  office: '',
  serviceLine: '',
  people: [],
  projects: [],
};

/**
 * The number of individually-selected filter values — the number the Filters badge shows and
 * the number of chips in the active-filter row. Those two must never disagree: a badge that
 * counts facets while the row shows values is the kind of small lie that costs trust.
 *
 * Deliberately defined as the chip count rather than as its own sum, so the two cannot drift
 * apart when a filter facet is added. `All regions` / `All offices` / `All service lines` are
 * the empty string — the absence of a filter, not a filter — and contribute no chip, hence 0.
 */
export function filterCount(v: FilterValue): number {
  return filterChips(v).length;
}

export interface FilterChip {
  /** Stable React key. */
  key: string;
  /** What the chip reads, e.g. `Gulf Coast` or `020417 - Q3 RATA`. */
  label: string;
  /** The filter value with just this one selection dropped. */
  without: FilterValue;
}

/**
 * One chip per selected value, in the order the filters sheet lists them: region, office,
 * service line, then each worker, then each project.
 *
 * `regionLabel` exists because the region filter's value is a region *code* — the chip should
 * say `Gulf Coast`, not `R3`.
 */
export function filterChips(v: FilterValue, regionLabel?: string): FilterChip[] {
  const chips: FilterChip[] = [];

  if (v.region) {
    chips.push({ key: 'region', label: regionLabel || v.region, without: { ...v, region: '' } });
  }
  if (v.office) {
    chips.push({ key: 'office', label: v.office, without: { ...v, office: '' } });
  }
  if (v.serviceLine) {
    chips.push({ key: 'serviceLine', label: v.serviceLine, without: { ...v, serviceLine: '' } });
  }
  for (const p of v.people) {
    chips.push({
      key: `person-${p.id}`,
      label: p.label,
      without: { ...v, people: v.people.filter((x) => x.id !== p.id) },
    });
  }
  for (const p of v.projects) {
    chips.push({
      key: `project-${p.id}`,
      label: p.label,
      without: { ...v, projects: v.projects.filter((x) => x.id !== p.id) },
    });
  }

  return chips;
}

function sameIds(a: { id: number }[], b: { id: number }[]): boolean {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map((x) => x.id));
  return b.every((x) => ids.has(x.id));
}

/** True when two filter values would produce the same query — used to label Apply vs Done. */
export function sameFilters(a: FilterValue, b: FilterValue): boolean {
  return (
    a.region === b.region &&
    a.office === b.office &&
    a.serviceLine === b.serviceLine &&
    sameIds(a.people, b.people) &&
    sameIds(a.projects, b.projects)
  );
}
